import { useScrollReveal } from "@/hooks/useScrollReveal";
import { experiences } from "@/data/experience";

function formatDate(date: string): string {
  const [year, month] = date.split("-");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[parseInt(month) - 1]} ${year}`;
}

export function Experience() {
  const headerRef = useScrollReveal<HTMLDivElement>({ y: 30 });

  return (
    <section id="experience" className="min-h-screen px-6 py-24">
      <div className="max-w-3xl mx-auto">
        {/* Section header */}
        <div ref={headerRef} className="mb-12">
          <div data-reveal className="flex items-center gap-3 mb-2">
            <span className="text-terminal-green">$</span>
            <span className="text-terminal-cyan">ls -la</span>
            <span className="text-foreground">./experience/</span>
          </div>
          <h2 data-reveal className="text-2xl font-bold text-terminal-bright">
            Career Roadmap
          </h2>
          <p data-reveal className="text-muted-foreground mt-1">
            {experiences.length} entries found
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[7px] top-0 bottom-0 w-px bg-border" />

          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <TimelineEntry key={exp.id} exp={exp} index={index} />
            ))}

            {/* Timeline start marker */}
            <div className="relative flex items-center gap-4 pl-8">
              <div className="absolute left-0 w-[15px] h-[15px] rounded-full bg-terminal-dim border-2 border-background" />
              <span className="text-terminal-dim text-sm">~/career init</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TimelineEntry({
  exp,
  index,
}: {
  exp: (typeof experiences)[number];
  index: number;
}) {
  const ref = useScrollReveal<HTMLDivElement>({ y: 30, delay: index * 0.05 });

  const isActive = exp.endDate === null;

  return (
    <div ref={ref} className="relative pl-8">
      {/* Timeline dot */}
      <div
        className={`absolute left-0 w-[15px] h-[15px] rounded-full border-2 border-background ${
          isActive ? "bg-terminal-green" : "bg-terminal-cyan"
        }`}
      />

      <div
        data-reveal
        className="border border-border rounded-lg p-5 bg-card hover:border-terminal-green/30 transition-colors"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="font-bold text-terminal-bright">{exp.role}</h3>
            <p className="text-terminal-cyan text-sm">@ {exp.company}</p>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
            {formatDate(exp.startDate)} —{" "}
            {exp.endDate ? (
              formatDate(exp.endDate)
            ) : (
              <span className="text-terminal-green">present</span>
            )}
          </span>
        </div>

        <p className="text-muted-foreground text-sm mb-3">{exp.description}</p>

        {/* Achievements */}
        <ul className="space-y-1 mb-3">
          {exp.achievements.map((achievement, i) => (
            <li key={i} className="text-sm text-foreground flex gap-2">
              <span className="text-terminal-green shrink-0">→</span>
              <span>{achievement}</span>
            </li>
          ))}
        </ul>

        {/* Tech stack */}
        <div className="flex flex-wrap gap-1.5">
          {exp.techStack.map((tech) => (
            <span
              key={tech}
              className="text-xs px-2 py-0.5 rounded bg-secondary text-terminal-cyan"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
